"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";
import Link from "next/link";
import Image from "next/image";

export function CookieConsent() {
  const t = useTranslations("Cookie");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (consent !== "true") {
      // Small delay for entrance animation
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookie-consent", "true");
    setIsVisible(false);
  };

  const handleDiscard = () => {
    localStorage.setItem("cookie-consent", "false");
    setIsVisible(false);
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} // smooth premium ease
          className="fixed bottom-5 left-5 z-80 w-full max-w-87.5"
        >
          {/* 
            Legacy Replication:
            - Width: 320px
            - Radius: 6px
            - Background: #f9fafc (light), adapted for dark
            - Shadow: 0 4px 4px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.08)
          */}
          <div
            className="bg-[#f9fafc] dark:bg-[#181818] rounded-md relative overflow-hidden"
            style={{
              boxShadow:
                "0 4px 4px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.08)",
            }}
          >
            {/* Close button – circular, soft */}
            <div className="absolute top-0 right-0 m-1.25 p-2 z-20">
              <button
                onClick={handleClose}
                className="max-w-5.25 max-h-5.25 p-0.5 rounded-full flex items-center justify-center bg-[#969798] dark:bg-[#4c4c4c] hover:bg-[red] text-white transition-colors cursor-pointer opacity-90 hover:opacity-100"
                aria-label={t("close_button")}
              >
                <X size={21} />
              </button>
            </div>

            {/* Content Area */}
            <div className="p-5 pb-2">
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="shrink-0 mt-1">
                  <Image
                    src="/images/cookie.png"
                    alt="Cookie"
                    width={45}
                    height={45}
                    className="object-contain"
                  />
                </div>

                {/* Text: font-size 15px, color #303030, pr-6 to avoid close button overlap */}
                <p className="text-[15px] leading-[1.3] text-[#303030] dark:text-[#e0e0e0] pt-1 pr-4">
                  {t("message")}
                  <br />
                  <Link
                    href="/privacy-policy"
                    className="text-(--brand-primary) font-semibold hover:underline mt-1 inline-block"
                  >
                    {t("privacy")}
                  </Link>
                </p>
              </div>
            </div>

            {/* Buttons: Legacy 'btn-small' & 'btn-passive' styles */}
            <div className="flex gap-2 p-3 pt-2">
              <button
                onClick={handleDiscard}
                className="flex-1 bg-transparent border border-(--border-subtle) dark:border-white/10 text-(--text-secondary) hover:text-(--text-primary)/90 hover:border-(--text-primary) dark:hover:border-white/90 dark:hover:bg-white/5 hover:bg-(--text-primary)/5 transition-colors duration-200 py-2.25 px-3.5 rounded-sm font-semibold text-base leading-tight text-center cursor-pointer outline-none"
              >
                {t("discard")}
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 bg-(--brand-primary) text-white hover:bg-(--brand-primary)/80 transition-colors duration-200 py-2.25 px-3.5 rounded-sm font-semibold text-base leading-tight text-center cursor-pointer shadow-sm border-none outline-none"
              >
                {t("accept")}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
